<script>

import FiltersPanel from "@/components/filters/FiltersPanel.vue";
import {BButton} from "buefy";
import EnrollTrainingModal from "@/components/trainings/EnrollTrainingModal.vue";
import TrainingThumbnail from "@/components/trainings/thumbnail/TrainingThumbnail.vue";
import { SUPPORTED_LOCALES } from "@/utils/locale.js";

export default {
    components: {
        TrainingThumbnail, EnrollTrainingModal, BButton, FiltersPanel
    },
    data() {
        return {
            loading: false,
            loadingCategories: true,
            loadingTags: true,
            firstLoading: true,
            trainings: [],
            meta: {
                current_page: 1,
                total: 0,
                per_page: 0,
            },
            controller: {
                data: null,
                categories: null,
                tags: null
            },
            filters: {},
            enrollModalOpened: false,
            selectedTraining: null,
            categories: [],
            tags: [],
            users: [],
            filterSelected: false
        }
    },
    computed: {
        filterEnrolled() {
            return this.$route.name === 'trainings.my';
        },
        filtersDef() {
            return[
                { key: 'name', label: this.$t('trainings.fields.name.name'), type: 'text' },
                { key: 'tag', label: this.$t('trainings.fields.tags.name'), type: 'trainingTags' },
                { key: 'price', label: this.$t('trainings.fields.price.name'), type: 'number' },
                { key: 'created_at', label: this.$t('trainings.fields.created_at.name'), type: 'date' },
                { key: 'training_category_id', 'label': this.$t('trainings.fields.category.name'), type: 'select', options: this.categories},
                { key: 'user_id', 'label': this.$t('trainings.fields.user.name'), type: 'select', options: this.users},
                { key: 'language', label: this.$t('trainings.fields.language.name'), type: 'select', options: this.languageOptions },
            ]
        },
        languageOptions() {
            return SUPPORTED_LOCALES.map(code => ({
                value: code,
                label: this.$t(`languages.${code}`)
            }));
        },
        canManage() {
            return this.$auth.isEmployer() || this.$auth.isAdmin() || this.$auth.isTrainer();
        }
    },
    methods: {
        onFiltersChange(filters) {
            this.filters = filters;
            this.meta.current_page = 1;
            this.loadData();
        },
        loadData(enableLoader = true) {
            if (this.controller.data) {
                this.controller.data.abort();
            }
            this.controller.data = new AbortController();

            this.loading = enableLoader;
            if (this.meta.current_page > 1 && enableLoader) {
                window.scrollTo({top: 0});
            }
            if (enableLoader) {
                this.trainings = [];
            }

            const params = {
                page: this.meta.current_page,
                filters: this.filters
            };

            if (this.filterEnrolled) {
                params.enrolled = 1;
            }

            axios.get('/trainings', {
                signal: this.controller.data.signal,
                params: params
            }).then(response => {
                this.trainings = response.data.data;
                this.meta.current_page = response.data.meta.current_page;
                this.meta.total = response.data.meta.total;
                this.meta.per_page = response.data.meta.per_page;
            }).catch(() => {}).finally(() => {
                this.loading = false;
            })
        },
        loadCategories() {
            this.controller.categories = new AbortController();
            this.loadingCategories = true;
            axios.get('/training-categories', {
                signal: this.controller.categories.signal,
                params: {
                    sort: 'name',
                    order: 'asc',
                    all: 1
                }
            }).then(response => {
                const categories = response.data.data;
                this.categories = categories.map(c => ({ label: c.name, value: c.id }));
            }).catch(() => {
            }).finally(() => {
                this.loadingCategories = false;
            });
        },
        loadTags() {
            this.controller.tags = new AbortController();
            this.loadingTags = true;
            axios.get('/training-tags', {
                signal: this.controller.tags.signal,
                params: {
                    sort: 'name',
                    order: 'asc',
                    all: 1
                }
            }).then(response => {
                this.tags = response.data.data;
            }).catch(() => {
            }).finally(() => {
                this.loadingTags = false;
            });
        },
        loadAuthors() {
            this.controller.users = new AbortController();
            this.loadingTags = true;
            axios.get('/users/training-authors', {
                signal: this.controller.users.signal
            }).then(response => {
                const users = response.data.data;
                this.users = users
                    .map(c => ({ label: c.full_name, value: c.id }));
            }).catch(() => {
            }).finally(() => {
                this.loadingTags = false;
            });
        },
        paginate(page) {
            this.meta.current_page = page;
            this.loadData();
        },
        addTraining() {
            this.$router.push({name: 'trainings.create'});
        },
        addTrainingSeries() {
            this.$router.push({name: 'training-series.create'});
        },
        showAll() {
            this.$router.push({name: 'trainings.index'});
        },
        showEnrolled() {
            this.$router.push({name: 'trainings.my'});
        },
        onEnrollModalOpen(training) {
            this.enrollModalOpened = true;
            this.selectedTraining = training;
        },
        onEnrollModalClose() {
            this.enrollModalOpened = false;
            this.selectedTraining = null;
        },
        onEnroll() {
            this.onEnrollModalClose();
            this.loadData(false)
        },
        selectCategory(id) {
            this.filterSelected = true;
            this.$refs.filtersPanel.setFilter('training_category_id', id);
            this.filters.training_category_id = id;
        },
        selectTag(tag) {
            this.filterSelected = true;
            this.$refs.filtersPanel.setFilter('tag', tag);
            this.filters.tags = tag;
        },
        showAllTrainings() {
            this.filterSelected = true;
            this.loadData();
        }
    },
    mounted() {
        this.loadCategories();
        this.loadTags();
        this.loadAuthors();
    },
    watch: {
        '$route'() {
            this.meta.current_page = 1;
            this.loadData();
        }
    },
    beforeUnmount() {
        this.controller.data && this.controller.data.abort();
        this.controller.categories && this.controller.categories.abort();
        this.controller.tags && this.controller.tags.abort();
    }
}

</script>
<template>
    <div class="trainings-index">

        <div v-if="!filterSelected && (canManage)" class="mb-4 buttons">
            <b-button type="is-link"
                      icon-left="plus"
                      @click="addTraining">
                {{ $t('trainings.labels.add_training') }}
            </b-button>
            <b-button type="is-link"
                      icon-left="playlist-plus"
                      @click="addTrainingSeries">
                {{ $t('training_series.labels.add_series') }}
            </b-button>
        </div>

        <div v-if="!filterSelected && !loadingCategories && !loadingTags" class="mb-6">
            <div class="tags">
                <a v-for="category in categories"
                   :key="category.value"
                   class="tag is-large p-6 category-name"
                   @click="selectCategory(category.value)"
                >
                    {{ category.label }}
                </a>
            </div>

            <div class="tags">
                <a v-for="tag in tags"
                   :key="tag.id"
                   @click="selectTag(tag.name)"
                   class="tag is-small" >
                    {{ tag.name }}
                </a>
            </div>
        </div>

        <div v-show="filterSelected" class="mb-4">
            <FiltersPanel
                ref="filtersPanel"
                :filters="filtersDef"
                @update:activeFilters="onFiltersChange"
            >
                <template #buttons>
                    <b-button type="is-link"
                              icon-left="plus"
                              @click="addTraining"
                              v-if="canManage">
                        {{ $t('trainings.labels.add_training') }}
                    </b-button>
                    <b-button type="is-link"
                              icon-left="playlist-plus"
                              @click="addTrainingSeries"
                              v-if="canManage">
                        {{ $t('training_series.labels.add_series') }}
                    </b-button>
                </template>
            </FiltersPanel>
        </div>

        <div class="buttons" v-if="filterSelected && $auth.isEmployee()">
            <button
                class="button"
                :class="{'is-primary': !filterEnrolled}"
                @click="showAll">
                {{ $t('trainings.labels.all_trainings') }}
            </button>
            <button
                class="button"
                :class="{'is-primary': filterEnrolled}"
                @click="showEnrolled">
                {{ $t('trainings.labels.my_trainings') }}
            </button>
        </div>

        <loader :loading="loading || loadingCategories || loadingTags"></loader>

        <div v-if="filterSelected" class="pb-6">
            <TrainingThumbnail v-for="training in trainings"
                               @onEnrollModalOpen="onEnrollModalOpen"
                               :training="training"></TrainingThumbnail>
        </div>

        <div v-if="!filterSelected && !loadingCategories && !loadingTags">
            <b-button @click="showAllTrainings" type="is-primary is-fullwidth" size="is-medium">
                {{ $t('trainings.labels.all_trainings') }}
            </b-button>
        </div>

        <div class="block has-text-grey has-text-centered mb-4 mt-4" v-if="!loading && !trainings.length && filterSelected">
            {{ $t('trainings.labels.no_trainings') }}
        </div>

        <EnrollTrainingModal
            v-if="enrollModalOpened"
            :training="selectedTraining"
            @onClose="onEnrollModalClose"
            @onEnroll="onEnroll"
            />

        <b-table
            v-if="filterSelected"
            :data="[]"
            :loading="loading"

            :paginated="!loading && meta.total > meta.per_page"
            backend-pagination
            :total="loading ? 0 : meta.total"
            :per-page="meta.per_page"
            @page-change="paginate">
        </b-table>
    </div>
</template>
<style scoped lang="scss">
.category-name {
    color: var(--bulma-text-strong);
}
</style>
